<template>
  <div
    class="modal-backdrop-custom"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog-custom card shadow">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span class="fw-semibold">Edit Invoice</span>
        <button
          class="btn-close"
          @click="$emit('close')"
        />
      </div>

      <div class="card-body p-0 modal-body-scroll">
        <div
          v-if="loadingDetails"
          class="text-center py-4"
        >
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          />
        </div>

        <template v-else>
          <div class="accordion-section">
            <button
              class="accordion-toggle"
              @click="sections.details = !sections.details"
            >
              <span class="fw-semibold">Invoice Details</span>
              <i :class="sections.details ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
            </button>
            <div
              v-if="sections.details"
              class="accordion-body"
            >
              <div class="row g-3">
                <div class="col-sm-6">
                  <label class="form-label">Invoice Number</label>
                  <input
                    v-model="form.invoiceNumber"
                    type="text"
                    class="form-control form-control-sm"
                  >
                </div>
                <div class="col-sm-6">
                  <label class="form-label">Issue Date</label>
                  <input
                    v-model="form.issueDate"
                    type="date"
                    class="form-control form-control-sm"
                    @change="recalcDueDate"
                  >
                </div>
                <div class="col-sm-6">
                  <label class="form-label">Terms (days)</label>
                  <input
                    v-model.number="form.termsDays"
                    v-no-wheel
                    type="number"
                    min="1"
                    class="form-control form-control-sm"
                    @input="recalcDueDate"
                  >
                </div>
                <div class="col-sm-6">
                  <label class="form-label">Due Date</label>
                  <input
                    v-model="form.dueDate"
                    type="date"
                    class="form-control form-control-sm"
                    :readonly="form.termsDays > 0"
                  >
                </div>
                <div class="col-sm-6">
                  <label class="form-label">Client Name</label>
                  <input
                    v-model="form.clientName"
                    type="text"
                    class="form-control form-control-sm"
                  >
                </div>
                <div class="col-sm-6">
                  <label class="form-label">Client Address</label>
                  <textarea
                    v-model="form.clientAddress"
                    class="form-control form-control-sm"
                    rows="2"
                  />
                </div>
                <div
                  v-for="p in form.projectCodes"
                  :key="p.project_id"
                  class="col-sm-6"
                >
                  <label class="form-label">
                    Project Code<span v-if="form.projectCodes.length > 1"> &mdash; {{ p.project_title }}</span>
                  </label>
                  <input
                    v-model="p.project_code"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Optional"
                  >
                </div>
              </div>

              <div class="mt-3">
                <label class="form-label fw-semibold">Experts</label>
                <div class="table-responsive">
                  <table class="table table-sm align-middle mb-0 line-items-table">
                    <thead>
                      <tr>
                        <th style="width: 5%;">
                          #
                        </th>
                        <th style="width: 22%;">
                          Expert
                        </th>
                        <th style="width: 18%;">
                          Type
                        </th>
                        <th style="width: 14%;">
                          Credits
                        </th>
                        <th style="width: 14%;">
                          Rate
                        </th>
                        <th
                          class="text-end"
                          style="width: 18%;"
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, idx) in form.items"
                        :key="item.id"
                      >
                        <td>{{ idx + 1 }}</td>
                        <td class="text-nowrap">
                          {{ item.expert_name }}
                        </td>
                        <td>
                          <select
                            v-model="item.type"
                            class="form-select form-select-sm"
                          >
                            <option value="">
                              — Select type —
                            </option>
                            <option
                              v-for="pref in engagementPreferences"
                              :key="pref.id"
                              :value="pref.name"
                            >
                              {{ pref.name }}
                            </option>
                          </select>
                        </td>
                        <td>
                          <input
                            v-model.number="item.credits"
                            v-no-wheel
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control form-control-sm text-end"
                          >
                        </td>
                        <td>
                          <input
                            v-model.number="item.rate"
                            v-no-wheel
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control form-control-sm text-end"
                          >
                        </td>
                        <td>
                          <input
                            v-model.number="item.amount"
                            v-no-wheel
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            class="form-control form-control-sm text-end"
                          >
                        </td>
                      </tr>
                      <tr class="total-row">
                        <td
                          colspan="5"
                          class="text-end fw-semibold"
                        >
                          Total
                        </td>
                        <td class="text-end fw-semibold">
                          {{ formatAmount(itemsTotal) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="accordion-section border-bottom-0">
            <button
              class="accordion-toggle"
              @click="sections.status = !sections.status"
            >
              <span class="fw-semibold">Status</span>
              <i :class="sections.status ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
            </button>
            <div
              v-if="sections.status"
              class="accordion-body"
            >
              <select
                v-model="selectedStatus"
                class="form-select form-select-sm"
              >
                <option :value="null">
                  &mdash; No status &mdash;
                </option>
                <option value="send_to_client">
                  Send to Client
                </option>
                <option value="paid">
                  Paid
                </option>
              </select>
            </div>
          </div>
        </template>
      </div>

      <div class="card-footer d-flex justify-content-end gap-2">
        <button
          class="btn btn-secondary btn-sm"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary btn-sm"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? 'Saving\u2026' : 'Save' }}
        </button>
        <button
          class="btn btn-success btn-sm"
          :disabled="savingAndDownload"
          @click="saveAndDownload"
        >
          {{ savingAndDownload ? 'Saving\u2026' : 'Save & Re-download PDF' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
    name: "InvoiceStatusModal",
    props: {
        invoice: {
            type: Object,
            default: null,
        },
    },
    emits: ["close", "invoice-updated"],
    data() {
        return {
            selectedStatus: null,
            loadingDetails: false,
            saving: false,
            savingAndDownload: false,
            sections: {
                details: true,
                status: false,
            },
            engagementPreferences: [],
            form: {
                invoiceNumber: null,
                issueDate: null,
                termsDays: 30,
                dueDate: null,
                clientName: null,
                clientAddress: null,
                projectCodes: [],
                items: [],
            },
        };
    },
    computed: {
        itemsTotal() {
            return this.form.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        },
    },
    watch: {
        invoice: {
            immediate: true,
            handler(invoice) {
                this.selectedStatus = invoice ? (invoice.invoice_status ?? null) : null;
                if (invoice) {
                    this.loadInvoiceDetails();
                }
            },
        },
    },
    methods: {
        loadInvoiceDetails() {
            this.loadingDetails = true;
            axios.get(`/admin/invoice/edit/${this.invoice.id}`)
                .then(({ data }) => {
                    const d = data.data;
                    this.form.invoiceNumber = d.invoice_number;
                    this.form.issueDate = d.issue_date;
                    this.form.dueDate = d.due_date;
                    this.form.clientName = d.client_name;
                    this.form.clientAddress = d.client_address;

                    const termsMatch = (d.terms || '').match(/Net\s+(\d+)/);
                    this.form.termsDays = termsMatch ? parseInt(termsMatch[1], 10) : 30;

                    this.form.projectCodes = (d.projects || []).map((p) => ({ ...p }));
                    this.form.items = (d.items || []).map((item) => ({ ...item }));
                    this.engagementPreferences = data.meta?.engagement_preferences || [];
                })
                .finally(() => { this.loadingDetails = false; });
        },
        recalcDueDate() {
            if (this.form.issueDate && this.form.termsDays > 0) {
                const dt = new Date(this.form.issueDate);
                dt.setDate(dt.getDate() + this.form.termsDays);
                const y = dt.getFullYear();
                const m = String(dt.getMonth() + 1).padStart(2, "0");
                const d = String(dt.getDate()).padStart(2, "0");
                this.form.dueDate = `${y}-${m}-${d}`;
            }
        },
        buildPatchPayload() {
            const payload = {
                invoice_status: this.selectedStatus,
                invoice_number: this.form.invoiceNumber,
                issue_date: this.form.issueDate || null,
                due_date: this.form.dueDate || null,
                terms_days: this.form.termsDays,
                client_name: this.form.clientName,
                client_address: this.form.clientAddress,
                project_codes: this.form.projectCodes.map((p) => ({
                    project_id: p.project_id,
                    project_code: p.project_code,
                })),
                items: this.form.items.map((item) => ({
                    id: item.id,
                    type: item.type,
                    credits: item.credits,
                    rate: item.rate,
                    amount: parseFloat(item.amount) || 0,
                })),
            };

            return payload;
        },
        save() {
            this.saving = true;
            axios.patch(`/admin/invoice/edit/${this.invoice.id}`, this.buildPatchPayload())
                .then(({ data }) => {
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "success",
                            icon: "fa fa-check me-1",
                            message: "Invoice updated successfully.",
                        });
                    }
                    this.$emit("invoice-updated", data.data);
                    this.$emit("close");
                })
                .catch(() => {
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "danger",
                            icon: "fa fa-times me-1",
                            message: "Failed to save invoice.",
                        });
                    }
                })
                .finally(() => { this.saving = false; });
        },
        saveAndDownload() {
            this.savingAndDownload = true;
            axios.patch(`/admin/invoice/edit/${this.invoice.id}`, this.buildPatchPayload())
                .then(({ data }) => {
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "success",
                            icon: "fa fa-check me-1",
                            message: "Invoice updated successfully.",
                        });
                    }
                    this.$emit("invoice-updated", data.data);
                    return axios.get(`/admin/invoice/${this.invoice.id}/download`, {
                        responseType: "blob",
                    }).then((response) => {
                        const disposition = response.headers["content-disposition"] || "";
                        const filenameMatch = /filename="?([^"]+)"?/.exec(disposition);
                        const filename = filenameMatch ? filenameMatch[1] : "invoice.pdf";

                        const blob = new Blob([response.data], { type: "application/pdf" });
                        const fileUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = fileUrl;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(fileUrl);

                        if (typeof One !== "undefined") {
                            One.helpers("jq-notify", {
                                type: "success",
                                icon: "fa fa-check me-1",
                                message: "PDF downloaded successfully.",
                            });
                        }

                        this.$emit("close");
                    });
                })
                .catch(() => {
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "danger",
                            icon: "fa fa-times me-1",
                            message: "Failed to save invoice.",
                        });
                    }
                })
                .finally(() => { this.savingAndDownload = false; });
        },
        formatAmount(value) {
            return Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
    },
};
</script>

<style scoped>
.modal-backdrop-custom {
    align-items: center;
    background: rgba(0, 0, 0, 0.45);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    padding: 1rem;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1050;
}

.modal-dialog-custom {
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 2rem);
    max-width: 720px;
    width: 100%;
}

.modal-dialog-custom > .card-header,
.modal-dialog-custom > .card-footer {
    flex: 0 0 auto;
}

.modal-body-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.accordion-section {
    border-bottom: 1px solid #e8ecec;
}

.accordion-toggle {
    align-items: center;
    background: none;
    border: none;
    color: #1F2937;
    display: flex;
    font-size: .88rem;
    justify-content: space-between;
    padding: .85rem 1.25rem;
    width: 100%;
}

.accordion-toggle:hover {
    background: #f7f9f8;
}

.accordion-body {
    padding: 0 1.25rem 1rem;
}

.line-items-table {
    font-size: .82rem;
}

.line-items-table thead th {
    background: #f7f9f8;
    border-bottom: 1px solid #e8ecec;
    color: #718088;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .06em;
    padding: .5rem .5rem;
    text-transform: uppercase;
    white-space: nowrap;
}

.line-items-table tbody td {
    padding: .4rem .5rem;
    vertical-align: middle;
}

.line-items-table input {
    min-width: 70px;
}

.total-row td {
    background: #eef2fb;
    border-top: 1px solid #dbe3f7;
}
</style>
